/**
 * Bible reference parsing — parse verse reference strings into BibleLocation.
 */
import { TOC } from '../constants/toc.js';
// Re-export all related utilities for barrel compatibility
export * from './format-reference.js';
export * from './chapter-navigation.js';
/**
 * Parse a book-scoped search query like "cloud in Hebrews" or "faith from Romans".
 * Returns the book info and the keyword remainder, or null if no scope detected.
 */
export function parseBookScope(query) {
    const scopePattern = /\b(?:in|from|within)\s+((?:\d\s)?[A-Za-z]+(?:\s[A-Za-z]+)?)\s*$/i;
    const bookOfPattern = /\b(?:book\s+of)\s+((?:\d\s)?[A-Za-z]+(?:\s[A-Za-z]+)?)\b/i;
    for (const pattern of [scopePattern, bookOfPattern]) {
        const match = query.match(pattern);
        if (!match)
            continue;
        const candidate = match[1].trim().toLowerCase();
        const bookInfo = TOC.bookAbbrevMap.get(candidate);
        if (bookInfo) {
            const remainder = query.replace(match[0], '').trim();
            return { bookInfo, remainder };
        }
    }
    return null;
}
/**
 * Parse a verse reference string into a BibleLocation.
 * Supports formats like "John 3:16", "Jhn 3:16", "Gen 1:1-5"
 * Returns null if parsing fails.
 */
export function parseReference(ref) {
    const match = ref.match(/^(\d?\s*[A-Za-z]+(?:\s+[A-Za-z]+)*)\s+(\d+):(\d+)/);
    if (!match)
        return null;
    const [, bookStr, chapterStr, verseStr] = match;
    const chapter = parseInt(chapterStr, 10) - 1;
    const verse = parseInt(verseStr, 10) - 1;
    if (isNaN(chapter) || isNaN(verse) || chapter < 0 || verse < 0) {
        return null;
    }
    const normalizedBook = bookStr.trim().toLowerCase();
    const info = TOC.bookAbbrevMap.get(normalizedBook);
    if (!info)
        return null;
    return {
        testamentIndex: info.testamentIndex,
        bookIndex: info.bookIndex,
        chapterIndex: chapter,
        verseIndex: verse,
    };
}
/**
 * Permissive reference parser for navigation: accepts "John 3:16", "Rom 8:28",
 * "1 Jn 4:8", "Romans 8" (chapter-only, verse defaults to 1), and tolerates
 * trailing punctuation/whitespace. Clamps chapter/verse to book bounds.
 * Returns null if the book can't be resolved or input doesn't look like a ref.
 */
export function parseNavReference(input) {
    const trimmed = input.trim();
    if (!trimmed)
        return null;
    const match = trimmed.match(/^(\d?\s*[A-Za-z]+(?:\s+[A-Za-z]+)*)\s+(\d+)(?::(\d+))?/);
    if (!match)
        return null;
    const [, bookStr, chapterStr, verseStr] = match;
    const chapter = parseInt(chapterStr, 10) - 1;
    const verse = verseStr ? parseInt(verseStr, 10) - 1 : 0;
    if (isNaN(chapter) || isNaN(verse) || chapter < 0 || verse < 0)
        return null;
    const info = TOC.bookAbbrevMap.get(bookStr.trim().toLowerCase());
    if (!info)
        return null;
    const maxChapter = info.chapterCount - 1;
    const chapterIndex = Math.min(chapter, maxChapter);
    return {
        testamentIndex: info.testamentIndex,
        bookIndex: info.bookIndex,
        chapterIndex,
        verseIndex: verse,
    };
}
/** Parse "Book C:V" or "Book C:V-V" (single chapter). V6.2: pericope mode
 *  must resolve the WHOLE range — anchoring on verseRefs[0]'s first verse
 *  was the Revelation-letters flatness bug (NEXT_TRAINING.md appendix). */
export function parsePassageRange(ref) {
    const start = parseReference(ref);
    if (!start)
        return null;
    const rangeMatch = ref.match(/:\d+\s*[-–]\s*(\d+)\s*$/);
    if (!rangeMatch)
        return { start, endVerseIndex: start.verseIndex };
    const endVerseIndex = parseInt(rangeMatch[1], 10) - 1;
    if (isNaN(endVerseIndex) || endVerseIndex < start.verseIndex)
        return null;
    return { start, endVerseIndex };
}
//# sourceMappingURL=bible-utils.js.map